import { useQuery, useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import React, { useMemo } from 'react';
import { Redirect, useHistory, useRouteMatch } from 'react-router-dom';
import { StringParam, useQueryParams } from 'use-query-params';

import { getAdCampaign } from 'apis/ad-bookings/api';
import { getCampaign } from 'apis/restaurant-api/merchant-admin/campaigns/api';
import { CampaignType } from 'apis/restaurant-api/merchant-admin/campaigns/types';
import { ProductLine, Venue } from 'apis/restaurant-api/merchant-admin/venue/types';
import { convertMonetaryIntToDecimal } from 'common/utils/currencyDecimals';

import { CampaignsQueryKeys } from '../campaignsQueryKeys';
import { CampaignPageLayout } from '../components/CampaignPageLayout';
import { AdCampaignForm } from '../components/forms/AdCampaignForm/AdCampaignForm';
import BasketDiscountForm from '../components/forms/BasketDiscountForm/BasketDiscountForm';
import DeliveryDiscountForm from '../components/forms/DeliveryDiscountForm/DeliveryDiscountForm';
import ItemDiscountForm from '../components/forms/ItemDiscountForm/ItemDiscountForm';
import { useCampaignModuleContext } from '../context';
import { useAdCampaignMutations } from '../hooks/useAdCampaignMutations';
import { useAdCampaignVenueConfiguration } from '../hooks/useAdCampaignVenueConfiguration';
import { useCampaignConfiguration } from '../hooks/useCampaignConfiguration';
import { useCampaignMutations } from '../hooks/useCampaignMutations';
import useCampaignsOptions from '../hooks/useCampaignsOptions';
import { useCampaignsRoutes } from '../hooks/useCampaignsRoutes';
import {
  AdCampaignFormValues,
  BidStrategy,
  CampaignAction,
  CampaignsFormValues,
  CampaignsRoutesParams,
  CampaignStatus,
} from '../types';
import { getCampaignActions } from '../utils/getCampaignActions';
import { getCampaignStatus } from '../utils/getCampaignStatus';
import { useTelemetryUpdateCampaignView } from '../utils/telemetry/telemetry';
import { transformCampaignApiToBasketDiscountForm } from '../utils/transformApiToForm/transformCampaignApiToBasketDiscountForm';
import { transformCampaignApiToDeliveryDiscountForm } from '../utils/transformApiToForm/transformCampaignApiToDeliveryDiscountForm';
import {
  transformCampaignApiToEndOfDayCampaignForm,
  transformCampaignApiToItemDiscountForm,
} from '../utils/transformApiToForm/transformCampaignApiToItemDiscountForm';

export type EditCampaignPageProps = {
  merchantId: string;
  venueIds?: string[];
  venueDetails?: Pick<Venue, 'name' | 'short_description' | 'listimage'>;
  campaignTypesPermittedToEdit: CampaignType[];
  timezone: string;
  country: Country;
  menuId?: string;
  productLines: ProductLine[];
};

export const EditCampaignPage = ({
  merchantId,
  venueIds = [],
  venueDetails,
  campaignTypesPermittedToEdit = [],
  timezone,
  country,
  menuId,
  productLines,
}: EditCampaignPageProps) => {
  const { currency } = useCampaignModuleContext();
  const { params } = useRouteMatch<CampaignsRoutesParams>();
  const history = useHistory();
  const { getCampaignTitleByType } = useCampaignsOptions();
  const campaignsRoutes = useCampaignsRoutes();
  const { deleteCampaign, terminate, updateEndDate, update } = useCampaignMutations({ merchantId, venueIds });
  const { update: updateAdCampaign } = useAdCampaignMutations({ venueIds });
  const { entityType } = useCampaignModuleContext();

  const queryClient = useQueryClient();
  const [queryParams] = useQueryParams({
    campaignType: StringParam,
  });

  const isAdCampaign = queryParams.campaignType === CampaignType.ADS;
  const isAdCampaignsEditable = campaignTypesPermittedToEdit.includes(CampaignType.ADS);

  const { data: campaign, isLoading: isPromotionLoading } = useQuery(
    CampaignsQueryKeys.GET_SINGLE(merchantId, params.campaignId),
    () =>
      getCampaign(merchantId, params.campaignId).then(campaign => ({
        ...campaign,
        campaign_type: campaign.is_end_of_day_campaign ? CampaignType.END_OF_DAY : campaign.campaign_type,
      })),
    {
      refetchOnWindowFocus: false,
      enabled: !isAdCampaign && !!merchantId && !!params.campaignId,
      retry: false,
    },
  );

  const { data: adCampaign, isLoading: isAdCampaignLoading } = useQuery(
    CampaignsQueryKeys.GET_SINGLE_ADS(venueIds[0], params.campaignId),
    () =>
      getAdCampaign(venueIds[0], params.campaignId).then(campaign => ({
        ...campaign,
        campaign_type: CampaignType.ADS,
        status: campaign.status.toLowerCase() as CampaignStatus,
        bid_strategy: (campaign.bid_strategy === 'CUSTOM' ? 'custom' : 'auto') as BidStrategy,
      })),
    {
      refetchOnWindowFocus: false,
      enabled: isAdCampaign && isAdCampaignsEditable && !!merchantId && !!params.campaignId,
      retry: false,
    },
  );

  const { data: venueAdsConfig, isLoading: isVenueAdConfigLoading } = useAdCampaignVenueConfiguration({
    venue: venueIds[0],
    country,
    enabled: isAdCampaign && isAdCampaignsEditable,
    retry: false,
  });

  const currentCampaign = campaign || adCampaign;

  const { configurationQuery, campaignTypesAvailableToEdit } = useCampaignConfiguration({
    merchantId,
    venueIds,
    campaignTypesPermittedToEdit,
  });

  const isCampaignLoading = (isPromotionLoading && !isAdCampaign) || (isAdCampaign && isAdCampaignLoading);
  const isConfigLoading = isAdCampaign ? isVenueAdConfigLoading : configurationQuery.isLoading;

  const isLoading =
    deleteCampaign.isLoading ||
    terminate.isLoading ||
    updateEndDate.isLoading ||
    update.isLoading ||
    isPromotionLoading ||
    isConfigLoading ||
    isCampaignLoading;

  const campaignStatus = isAdCampaign ? adCampaign?.status : getCampaignStatus(campaign);

  const defaultValues = useMemo<CampaignsFormValues | undefined>(() => {
    if (!campaign) {
      return;
    }

    if (campaign.campaign_type === CampaignType.DELIVERY_DISCOUNT) {
      return transformCampaignApiToDeliveryDiscountForm(campaign, timezone);
    }

    if (campaign.campaign_type === CampaignType.BASKET_DISCOUNT) {
      return transformCampaignApiToBasketDiscountForm(campaign, timezone);
    }

    if (campaign.campaign_type === CampaignType.ITEM_DISCOUNT) {
      return transformCampaignApiToItemDiscountForm(campaign, timezone);
    }

    if (campaign.campaign_type === CampaignType.END_OF_DAY) {
      return transformCampaignApiToEndOfDayCampaignForm(campaign, timezone);
    }

    return;
  }, [campaign, timezone]);

  const handleSubmit = (values: CampaignsFormValues) => {
    if (!configurationQuery.data || !campaign) {
      return;
    }

    if (campaignStatus !== CampaignStatus.SCHEDULED) {
      return updateEndDate.mutate({
        merchantId,
        campaignId: values.campaignId || params.campaignId,
        endDate: values.endDate,
      });
    }

    if (values.campaignType === CampaignType.DELIVERY_DISCOUNT) {
      const minBasketSize = values.minBasketSize || Number(Object.keys(configurationQuery.data.basket_discounts)[0]);
      const deliveryDiscountConfig = configurationQuery.data.delivery_discounts[minBasketSize];

      return update.mutate({
        ...values,
        currency,
        deliveryDiscount: {
          amount: deliveryDiscountConfig?.recommended_amount,
          percentage: deliveryDiscountConfig?.recommended_percentage,
        },
      });
    }

    if (values.campaignType === CampaignType.END_OF_DAY) {
      return update.mutate({ ...values, currency, campaignType: CampaignType.ITEM_DISCOUNT });
    }

    return update.mutate({ ...values, currency });
  };

  const handleDelete = (campaignId: string) =>
    deleteCampaign.mutate(
      { merchantId, campaignId },
      {
        onSuccess: () => history.push(campaignsRoutes.index),
      },
    );

  const handleTerminate = (campaignId: string) =>
    terminate.mutate(
      { merchantId, campaignId },
      {
        onSuccess: () => queryClient.invalidateQueries(CampaignsQueryKeys.GET_SINGLE(merchantId, params.campaignId)),
      },
    );

  const handleCancelEdit = () => history.push(campaignsRoutes.details(params.campaignId));

  const handleUpdateAdCampaign = (values: AdCampaignFormValues) => {
    // Need to include bidStrategy if customBidAmount is set so transformation takes the custom bid change into account
    // values might not have bidStrategy set if the user has not changed from custom -> automatic and vice versa, but only changed the bid amount
    if (values.customBidAmount) {
      values.bidStrategy = 'custom';
    }
    updateAdCampaign.mutate({ ...values, campaignId: params.campaignId, country, currency });
  };
  const handleCancelAdCampaign = () => {
    if (adCampaign) {
      history.push(`${campaignsRoutes.details(adCampaign.id)}?campaignType=Ads`);
    }
  };

  useTelemetryUpdateCampaignView(
    currentCampaign?.campaign_type,
    currentCampaign && configurationQuery.data && campaignTypesAvailableToEdit.includes(currentCampaign.campaign_type),
  );

  const availableActions: CampaignAction[] | undefined =
    campaignStatus &&
    currentCampaign &&
    getCampaignActions({
      campaignType: currentCampaign.campaign_type,
      campaignStatus,
      country,
      isCreatedByWolt: 'created_by_wolt' in currentCampaign ? currentCampaign.created_by_wolt : undefined,
      isMerchantLevel: entityType === 'merchant',
      venueIds: 'venue_ids' in currentCampaign ? currentCampaign.venue_ids : venueIds,
    });

  const isCampaignEditable = availableActions?.includes('edit');

  if (!isLoading && currentCampaign && !campaignTypesAvailableToEdit.includes(currentCampaign.campaign_type)) {
    return <Redirect to={campaignsRoutes.details(params.campaignId)} />;
  }

  const canAccessEditPage = () => {
    if (isAdCampaign && !isAdCampaignLoading) {
      return isAdCampaignsEditable;
    } else if (!isAdCampaign && !isCampaignLoading) {
      return isCampaignEditable;
    }
    return true;
  };

  if (!canAccessEditPage()) {
    return <Redirect to={campaignsRoutes.index} />;
  }

  const getAdCampaignBudgetType = () => {
    if (!adCampaign) {
      return;
    }

    const budget = convertMonetaryIntToDecimal(adCampaign.weekly_budget_amount, currency);
    const budgetType = venueAdsConfig?.budgetOptions.find(b => b.value === budget)?.type;
    return budgetType || 'custom';
  };

  return (
    <CampaignPageLayout
      heading={getCampaignTitleByType(currentCampaign?.campaign_type)}
      isLoading={isCampaignLoading || isConfigLoading}
      status={campaignStatus}
      venueCount={defaultValues?.venueIds.length}
    >
      {campaign && defaultValues && configurationQuery.data && (
        <>
          {defaultValues.campaignType === CampaignType.DELIVERY_DISCOUNT && (
            <DeliveryDiscountForm
              venueDetails={venueDetails}
              configuration={configurationQuery.data}
              timezone={timezone}
              country={country}
              isLoading={isPromotionLoading}
              availableActions={availableActions}
              onSubmit={handleSubmit}
              onDelete={handleDelete}
              onTerminate={handleTerminate}
              onCancelEdit={handleCancelEdit}
              campaignId={campaign.id}
              campaignStatus={campaignStatus}
              defaultValues={defaultValues}
            />
          )}

          {defaultValues.campaignType === CampaignType.BASKET_DISCOUNT && (
            <BasketDiscountForm
              venueDetails={venueDetails}
              campaignId={campaign.id}
              campaignStatus={campaignStatus}
              defaultValues={defaultValues}
              configuration={configurationQuery.data}
              timezone={timezone}
              country={country}
              isLoading={isPromotionLoading}
              availableActions={availableActions}
              onSubmit={handleSubmit}
              onDelete={handleDelete}
              onTerminate={handleTerminate}
              onCancelEdit={handleCancelEdit}
            />
          )}

          {defaultValues.campaignType === CampaignType.ITEM_DISCOUNT && menuId && (
            <ItemDiscountForm
              campaignId={campaign.id}
              campaignStatus={campaignStatus}
              defaultValues={defaultValues}
              configuration={configurationQuery.data}
              timezone={timezone}
              country={country}
              menuId={menuId}
              productLines={productLines}
              isLoading={isLoading}
              availableActions={availableActions}
              onSubmit={handleSubmit}
              onDelete={handleDelete}
              onTerminate={handleTerminate}
              onCancelEdit={handleCancelEdit}
            />
          )}
        </>
      )}
      {adCampaign && venueAdsConfig && (
        <AdCampaignForm
          campaignId={adCampaign.id}
          campaignAuthor={adCampaign.created_by}
          initialValues={{
            budgetAmount: convertMonetaryIntToDecimal(adCampaign.weekly_budget_amount, currency),
            bidStrategy: adCampaign.bid_strategy,
            customBidAmount:
              adCampaign.bid_strategy === 'custom'
                ? convertMonetaryIntToDecimal(adCampaign.bid_amount, currency)
                : undefined,
            startDate: DateTime.fromFormat(adCampaign.start_date, 'yyyy-MM-dd').toISO(),
            endDate: adCampaign.end_date ? DateTime.fromFormat(adCampaign.end_date, 'yyyy-MM-dd').toISO() : undefined,
            campaignType: CampaignType.ADS,
            campaignId: adCampaign.id,
            scheduleStrategy: adCampaign.end_date ? 'selectDates' : 'alwaysOn',
            agreementTermsAndConditions: true,
            budgetType: getAdCampaignBudgetType() || 'recommended',
          }}
          isLoading={isAdCampaignLoading || updateAdCampaign.isLoading}
          onUpdate={handleUpdateAdCampaign}
          onCancelEdit={handleCancelAdCampaign}
          venueAdsConfig={venueAdsConfig}
          campaignStatus={campaignStatus}
          venueDetails={venueDetails}
        />
      )}
    </CampaignPageLayout>
  );
};
